import { SupportedLanguage } from './translations';
import { ShipmentStatus } from '../types/shipment';

export const portalTranslations: Record<
  SupportedLanguage,
  {
    navPortal: string;
    tracker: {
      title: string;
      subtitle: string;
      declarationLabel: string;
      declarationPlaceholder: string;
      trackButton: string;
      hint: string;
      howToGet: string;
      notFound: string;
      contactWhatsApp: string;
      portalLink: string;
      currentStatus: string;
      lastUpdate: string;
      steps: Record<ShipmentStatus, string>;
    };
    portal: {
      title: string;
      subtitle: string;
      email: string;
      password: string;
      login: string;
      logout: string;
      welcome: string;
      myShipments: string;
      noShipments: string;
      declaration: string;
      cargo: string;
      border: string;
      status: string;
      updated: string;
      demoAccount: string;
      search: string;
      filterAll: string;
      viewDetails: string;
      forgotPassword: string;
      resetSent: string;
      backToList: string;
      timeline: string;
      documents: string;
      noDocuments: string;
      download: string;
    };
    admin: {
      title: string;
      subtitle: string;
      login: string;
      logout: string;
      allShipments: string;
      updateStatus: string;
      save: string;
      addShipment: string;
      clientEmail: string;
      clientName: string;
      clientPhone: string;
      declarationNumber: string;
      cargoDescription: string;
      border: string;
      note: string;
      created: string;
      search: string;
      edit: string;
      notifyWhatsApp: string;
      addClient: string;
      clientPassword: string;
      company: string;
      clientCode: string;
      uploadDoc: string;
      deleteDoc: string;
      wrongRole: string;
    };
    flipHint: string;
  }
> = {
  ar: {
    navPortal: 'تتبع الشحنات',
    tracker: {
      title: 'تتبع حالة البيان',
      subtitle: 'أدخل رقم البيان الجمركي لعرض مراحل التخليص من الوصول حتى التسليم',
      declarationLabel: 'رقم البيان الجمركي',
      declarationPlaceholder: 'مثال: SY-2026-004821',
      trackButton: 'عرض الحالة',
      hint: 'رقم البيان يظهر في أوراق التخليص أو رسالة التأكيد من الشراع',
      howToGet: 'لا تملك الرقم؟ تواصل معنا عبر واتساب أو سجّل دخولك في بوابة العملاء.',
      notFound: 'لم يُعثر على بيان بهذا الرقم.',
      contactWhatsApp: 'تواصل عبر واتساب',
      portalLink: 'دخول بوابة العملاء',
      currentStatus: 'الحالة الحالية',
      lastUpdate: 'آخر تحديث',
      steps: {
        arrived: 'وصول الشحنة',
        inspection: 'الكشف الجمركي',
        clearance: 'التخليص الجمركي',
        release: 'الإفراج',
        delivered: 'التسليم',
      },
    },
    portal: {
      title: 'بوابة العملاء',
      subtitle: 'تابع آخر حالة لبياناتك الجمركية',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      login: 'تسجيل الدخول',
      logout: 'تسجيل الخروج',
      welcome: 'مرحباً',
      myShipments: 'شحناتي',
      noShipments: 'لا توجد شحنات مسجّلة لحسابك بعد.',
      declaration: 'رقم البيان',
      cargo: 'البضاعة',
      border: 'المعبر',
      status: 'الحالة',
      updated: 'آخر تحديث',
      demoAccount: 'حساب تجريبي: client@demo.com / demo2024',
      search: 'بحث برقم البيان أو البضاعة',
      filterAll: 'كل الحالات',
      viewDetails: 'عرض التفاصيل',
      forgotPassword: 'نسيت كلمة المرور؟',
      resetSent: 'إن وُجد الحساب، سيصلك رابط إعادة التعيين على البريد.',
      backToList: 'العودة للشحنات',
      timeline: 'سجل المراحل',
      documents: 'المستندات',
      noDocuments: 'لا توجد مستندات مرفوعة بعد.',
      download: 'تحميل',
    },
    admin: {
      title: 'لوحة إدارة الشحنات',
      subtitle: 'تحديث حالة البيانات للعملاء',
      login: 'دخول الإدارة',
      logout: 'خروج',
      allShipments: 'جميع البيانات',
      updateStatus: 'تحديث الحالة',
      save: 'حفظ',
      addShipment: 'إضافة بيان',
      clientEmail: 'بريد العميل',
      clientName: 'اسم العميل',
      clientPhone: 'هاتف واتساب العميل',
      declarationNumber: 'رقم البيان',
      cargoDescription: 'وصف البضاعة',
      border: 'المعبر',
      note: 'ملاحظة للعميل',
      created: 'تمت الإضافة',
      search: 'بحث: رقم بيان / رمز تاجر / عميل / حالة',
      edit: 'تعديل البيانات',
      notifyWhatsApp: 'إشعار واتساب',
      addClient: 'إضافة عميل',
      clientPassword: 'كلمة مرور مؤقتة',
      company: 'الشركة',
      clientCode: 'رمز التاجر',
      uploadDoc: 'رفع مستند',
      deleteDoc: 'حذف',
      wrongRole: 'هذا حساب عميل — استخدم بوابة العملاء /portal',
    },
    flipHint: 'اضغط لقلب البطاقة',
  },
  en: {
    navPortal: 'Track Shipments',
    tracker: {
      title: 'Declaration Status Tracker',
      subtitle: 'Enter your customs declaration number to see clearance stages',
      declarationLabel: 'Customs declaration number',
      declarationPlaceholder: 'e.g. SY-2026-004821',
      trackButton: 'Show status',
      hint: 'The declaration number appears on your clearance papers or confirmation from Al Shiraa',
      howToGet: 'Don’t have the number? Contact us on WhatsApp or sign in to the client portal.',
      notFound: 'No declaration found with this number.',
      contactWhatsApp: 'Contact on WhatsApp',
      portalLink: 'Client portal login',
      currentStatus: 'Current status',
      lastUpdate: 'Last update',
      steps: {
        arrived: 'Arrival',
        inspection: 'Inspection',
        clearance: 'Clearance',
        release: 'Release',
        delivered: 'Delivered',
      },
    },
    portal: {
      title: 'Client Portal',
      subtitle: 'Track your latest customs declarations',
      email: 'Email',
      password: 'Password',
      login: 'Sign in',
      logout: 'Sign out',
      welcome: 'Welcome',
      myShipments: 'My shipments',
      noShipments: 'No shipments registered for your account yet.',
      declaration: 'Declaration',
      cargo: 'Cargo',
      border: 'Border',
      status: 'Status',
      updated: 'Last update',
      demoAccount: 'Demo: client@demo.com / demo2024',
      search: 'Search by declaration or cargo',
      filterAll: 'All statuses',
      viewDetails: 'View details',
      forgotPassword: 'Forgot password?',
      resetSent: 'If the account exists, a reset link will be emailed.',
      backToList: 'Back to shipments',
      timeline: 'Status history',
      documents: 'Documents',
      noDocuments: 'No documents uploaded yet.',
      download: 'Download',
    },
    admin: {
      title: 'Shipment Admin',
      subtitle: 'Update declaration status for clients',
      login: 'Admin login',
      logout: 'Logout',
      allShipments: 'All declarations',
      updateStatus: 'Update status',
      save: 'Save',
      addShipment: 'Add declaration',
      clientEmail: 'Client email',
      clientName: 'Client name',
      clientPhone: 'Client WhatsApp phone',
      declarationNumber: 'Declaration number',
      cargoDescription: 'Cargo description',
      border: 'Border',
      note: 'Note for client',
      created: 'Added',
      search: 'Search: declaration / trader code / client / status',
      edit: 'Edit details',
      notifyWhatsApp: 'WhatsApp notify',
      addClient: 'Add client',
      clientPassword: 'Temporary password',
      company: 'Company',
      clientCode: 'Trader code',
      uploadDoc: 'Upload document',
      deleteDoc: 'Delete',
      wrongRole: 'This is a client account — use /portal',
    },
    flipHint: 'Tap to flip',
  },
  de: {
    navPortal: 'Sendungen verfolgen',
    tracker: {
      title: 'Status der Zollanmeldung',
      subtitle: 'Geben Sie Ihre Anmeldungsnummer ein, um die Phasen zu sehen',
      declarationLabel: 'Zollanmeldungsnummer',
      declarationPlaceholder: 'z.B. SY-2026-004821',
      trackButton: 'Status anzeigen',
      hint: 'Die Nummer finden Sie auf Ihren Papieren oder in der Bestätigung von Al Shiraa',
      howToGet: 'Keine Nummer? Kontaktieren Sie uns per WhatsApp oder melden Sie sich im Portal an.',
      notFound: 'Keine Anmeldung mit dieser Nummer gefunden.',
      contactWhatsApp: 'Per WhatsApp kontaktieren',
      portalLink: 'Kundenportal',
      currentStatus: 'Aktueller Status',
      lastUpdate: 'Letzte Aktualisierung',
      steps: {
        arrived: 'Ankunft',
        inspection: 'Kontrolle',
        clearance: 'Abfertigung',
        release: 'Freigabe',
        delivered: 'Zustellung',
      },
    },
    portal: {
      title: 'Kundenportal',
      subtitle: 'Verfolgen Sie Ihre Zollanmeldungen',
      email: 'E-Mail',
      password: 'Passwort',
      login: 'Anmelden',
      logout: 'Abmelden',
      welcome: 'Willkommen',
      myShipments: 'Meine Sendungen',
      noShipments: 'Noch keine Sendungen für Ihr Konto.',
      declaration: 'Anmeldung',
      cargo: 'Ware',
      border: 'Grenze',
      status: 'Status',
      updated: 'Letzte Aktualisierung',
      demoAccount: 'Demo: client@demo.com / demo2024',
      search: 'Suche nach Nummer oder Ware',
      filterAll: 'Alle Status',
      viewDetails: 'Details anzeigen',
      forgotPassword: 'Passwort vergessen?',
      resetSent: 'Falls das Konto existiert, erhalten Sie eine E-Mail.',
      backToList: 'Zurück zu Sendungen',
      timeline: 'Statusverlauf',
      documents: 'Dokumente',
      noDocuments: 'Noch keine Dokumente hochgeladen.',
      download: 'Herunterladen',
    },
    admin: {
      title: 'Sendungsverwaltung',
      subtitle: 'Status für Kunden aktualisieren',
      login: 'Admin-Anmeldung',
      logout: 'Abmelden',
      allShipments: 'Alle Anmeldungen',
      updateStatus: 'Status aktualisieren',
      save: 'Speichern',
      addShipment: 'Anmeldung hinzufügen',
      clientEmail: 'Kunden-E-Mail',
      clientName: 'Kundenname',
      clientPhone: 'Kunden-WhatsApp',
      declarationNumber: 'Anmeldungsnummer',
      cargoDescription: 'Warenbeschreibung',
      border: 'Grenze',
      note: 'Notiz für Kunden',
      created: 'Hinzugefügt',
      search: 'Suche: Nummer / Händlercode / Kunde / Status',
      edit: 'Bearbeiten',
      notifyWhatsApp: 'WhatsApp benachrichtigen',
      addClient: 'Kunde hinzufügen',
      clientPassword: 'Temporäres Passwort',
      company: 'Firma',
      clientCode: 'Händlercode',
      uploadDoc: 'Dokument hochladen',
      deleteDoc: 'Löschen',
      wrongRole: 'Dies ist ein Kundenkonto — nutzen Sie /portal',
    },
    flipHint: 'Tippen zum Umdrehen',
  },
};
